"use client";

import { useAnalytics } from "@analytics";
import { PROMPT_GENERATOR_EVENTS } from "@repo/analytics";
import {
	PROMPT_GENERATOR_FRAMEWORKS,
	PROMPT_GENERATOR_FRAMEWORK_LABELS,
	PROMPT_GENERATOR_RESULT_PARAM,
	type PromptGeneratorFramework,
	stripWwwFromDomain,
} from "@repo/core/client";
import { LocationInput } from "@shared/components/LocationInput";
import type { LocationData } from "@shared/components/LocationInput/types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import { Checkbox } from "@ui/components/checkbox";
import { Input } from "@ui/components/input";
import { Switch } from "@ui/components/switch";
import { cn } from "@ui/lib";
import { Loader2Icon, SparklesIcon, UsersIcon } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { ParseResultType, fromUrl, parseDomain } from "parse-domain";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import {
	type PromptGenerationResult,
	PromptGeneratorApiError,
	fetchGenerationStatus,
	generatePrompts,
	posthogSessionId,
} from "../lib/api";
import { FRAMEWORK_FORM_DESCRIPTIONS, PERSONA_OPTION } from "../lib/copy";
import { GeneratingPanel, type GeneratingStep } from "./GeneratingPanel";
import { PromptResultsPanel } from "./PromptResultsPanel";

function validateDomain(input: string): {
	isValid: boolean;
	domain?: string;
	error?: string;
} {
	if (!input?.trim()) {
		return { isValid: false, error: "Please enter a domain" };
	}
	const parseResult = parseDomain(fromUrl(input.trim()));
	if (parseResult.type === ParseResultType.Listed) {
		let domain =
			`${parseResult.subDomains.join(".")}.${parseResult.domain}.${parseResult.topLevelDomains.join(".")}`.replace(
				/^\./,
				"",
			);
		domain = stripWwwFromDomain(domain);
		return { isValid: true, domain };
	}
	return {
		isValid: false,
		error: "Please enter a valid domain (e.g., example.com)",
	};
}

function isFramework(value: string): value is PromptGeneratorFramework {
	return (PROMPT_GENERATOR_FRAMEWORKS as readonly string[]).includes(value);
}

/**
 * Keep the result in the URL (`?generation=<id>`) without a navigation, so a
 * reload, a shared link or the results email lands on the same result.
 */
function writeGenerationParam(generationId: string | null) {
	if (typeof window === "undefined") {
		return;
	}
	const url = new URL(window.location.href);
	if (generationId) {
		url.searchParams.set(PROMPT_GENERATOR_RESULT_PARAM, generationId);
	} else {
		url.searchParams.delete(PROMPT_GENERATOR_RESULT_PARAM);
	}
	window.history.replaceState(null, "", url.toString());
}

/** How long the "building your snapshot" row stays active once running. */
const SNAPSHOT_STEP_MS = 15_000;
const POLL_MS = 2_000;

type Phase = "input" | "generating" | "results";

/**
 * The free AI visibility prompt generator: the form, the generating state,
 * and the results (prompts, personas, the optional email and the
 * "Track these personas" CTA). Owns its card, because the card widens from
 * the form width to the full content width when results arrive.
 */
export function PromptGeneratorForm() {
	const searchParams = useSearchParams();
	const { trackEvent } = useAnalytics();
	const personaSwitchId = useId();
	const formRef = useRef<HTMLDivElement>(null);

	const initialGenerationId =
		searchParams.get(PROMPT_GENERATOR_RESULT_PARAM)?.trim() || null;

	const [phase, setPhase] = useState<Phase>(
		initialGenerationId ? "generating" : "input",
	);
	const [generationId, setGenerationId] = useState<string | null>(
		initialGenerationId,
	);
	const [restored, setRestored] = useState(Boolean(initialGenerationId));
	const [domain, setDomain] = useState("");
	const [location, setLocation] = useState<LocationData | undefined>();
	const [validationError, setValidationError] = useState<string | null>(null);
	const [selectedFrameworks, setSelectedFrameworks] = useState<
		Set<PromptGeneratorFramework>
	>(() => new Set<PromptGeneratorFramework>(["original"]));
	const [includePersonas, setIncludePersonas] = useState(true);
	const [runningSince, setRunningSince] = useState<number | null>(null);
	const [now, setNow] = useState(() => Date.now());
	// Once-per-generation analytics (completed, personas viewed).
	const tracked = useRef(new Set<string>());

	const trackOnce = useCallback((key: string, fire: () => void) => {
		if (tracked.current.has(key)) {
			return;
		}
		tracked.current.add(key);
		fire();
	}, []);

	const resetToForm = useCallback((message?: string) => {
		setPhase("input");
		setGenerationId(null);
		setRunningSince(null);
		writeGenerationParam(null);
		if (message) {
			setValidationError(message);
		}
	}, []);

	const generateMutation = useMutation({
		mutationFn: () => {
			const validation = validateDomain(domain);
			if (!validation.isValid || !validation.domain) {
				throw new Error(validation.error || "Invalid domain");
			}
			const frameworks = Array.from(selectedFrameworks);
			trackEvent(PROMPT_GENERATOR_EVENTS.GENERATION_REQUESTED, {
				domain: validation.domain,
				frameworks,
				includePersonas,
				hasLocation: Boolean(location?.city || location?.region),
			});
			return generatePrompts({
				domain: validation.domain,
				frameworks,
				location,
				includePersonas,
				sessionId: posthogSessionId(),
			});
		},
		onSuccess: (data) => {
			setGenerationId(data.generationId);
			setRestored(false);
			setRunningSince(null);
			setPhase("generating");
			setValidationError(null);
			writeGenerationParam(data.generationId);
		},
		onError: (err: Error) => {
			setValidationError(err.message);
			trackEvent(PROMPT_GENERATOR_EVENTS.GENERATION_FAILED, {
				stage: "start",
				errorMessage: err.message,
			});
		},
	});

	const statusQuery = useQuery({
		queryKey: ["prompt-generation-public", generationId],
		queryFn: () => fetchGenerationStatus(generationId as string),
		enabled: phase !== "input" && Boolean(generationId),
		refetchInterval: (query) => {
			const data = query.state.data as PromptGenerationResult | undefined;
			if (!data) {
				return POLL_MS;
			}
			return data.status === "pending" || data.status === "running"
				? POLL_MS
				: false;
		},
		// An unknown id (a stale link) answers 404; say so at once.
		retry: (count, error) =>
			!(
				error instanceof PromptGeneratorApiError && error.status === 404
			) && count < 3,
	});
	const generation = statusQuery.data;
	const status = generation?.status;

	useEffect(() => {
		if (status === "running" && runningSince === null) {
			setRunningSince(Date.now());
		}
	}, [status, runningSince]);

	useEffect(() => {
		if (phase !== "generating") {
			return;
		}
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, [phase]);

	useEffect(() => {
		if (phase !== "generating" || !generation || !generationId) {
			return;
		}
		if (generation.status === "complete") {
			setPhase("results");
			const personaCount = generation.personaDrafts?.length ?? 0;
			trackOnce(`completed:${generationId}`, () =>
				trackEvent(PROMPT_GENERATOR_EVENTS.GENERATION_COMPLETED, {
					generationId,
					promptCount: generation.results?.length ?? 0,
					personaCount,
					includePersonas: generation.includePersonas,
					restored,
				}),
			);
		}
		if (generation.status === "failed") {
			const message = generation.error || "Generation failed";
			toast.error(message);
			trackEvent(PROMPT_GENERATOR_EVENTS.GENERATION_FAILED, {
				generationId,
				stage: "generation",
				errorMessage: message,
			});
			resetToForm();
		}
	}, [
		phase,
		generation,
		generationId,
		restored,
		resetToForm,
		trackEvent,
		trackOnce,
	]);

	useEffect(() => {
		if (!statusQuery.error || phase === "input") {
			return;
		}
		const notFound =
			statusQuery.error instanceof PromptGeneratorApiError &&
			statusQuery.error.status === 404;
		resetToForm(
			notFound
				? "We couldn't find those results. Generate them again below."
				: "We couldn't load those results. Try again below.",
		);
	}, [statusQuery.error, phase, resetToForm]);

	const toggleFramework = (fw: PromptGeneratorFramework) => {
		setSelectedFrameworks((prev) => {
			const next = new Set(prev);
			if (next.has(fw)) {
				next.delete(fw);
			} else {
				next.add(fw);
			}
			return next;
		});
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (selectedFrameworks.size === 0) {
			setValidationError("Select at least one prompt framework");
			return;
		}
		generateMutation.mutate();
	};

	/** A result without personas: back to the form, option on, same inputs. */
	const handleAddPersonas = () => {
		if (generation?.domain) {
			setDomain(generation.domain);
		}
		const frameworks = (generation?.frameworks ?? []).filter(isFramework);
		if (frameworks.length > 0) {
			setSelectedFrameworks(new Set(frameworks));
		}
		setIncludePersonas(true);
		setValidationError(null);
		resetToForm();
		formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const results = generation?.results ?? [];
	const personas = generation?.personaDrafts ?? [];
	const showResults =
		phase === "results" && generation && generationId && results.length > 0;
	const count = selectedFrameworks.size * 10;

	const step: GeneratingStep =
		!status || status === "pending"
			? 0
			: runningSince !== null && now - runningSince < SNAPSHOT_STEP_MS
				? 1
				: 2;

	let body: React.ReactNode;
	if (showResults) {
		body = (
			<PromptResultsPanel
				generationId={generationId}
				domain={generation.domain ?? domain}
				results={results}
				personas={personas}
				segments={generation.segments ?? []}
				emailSent={generation.emailSent}
				onAddPersonas={handleAddPersonas}
				onPersonasViewed={() =>
					trackOnce(`personas:${generationId}`, () =>
						trackEvent(PROMPT_GENERATOR_EVENTS.PERSONAS_VIEWED, {
							generationId,
							personaCount: personas.length,
						}),
					)
				}
				onExport={(format) =>
					trackEvent(PROMPT_GENERATOR_EVENTS.EXPORT_CLICKED, {
						generationId,
						format,
						promptCount: results.length,
						personaCount: personas.length,
					})
				}
				onEmailSent={() =>
					trackEvent(PROMPT_GENERATOR_EVENTS.EMAIL_SUBMITTED, {
						generationId,
					})
				}
				onTrackClick={() =>
					trackEvent(PROMPT_GENERATOR_EVENTS.TRACK_CTA_CLICKED, {
						generationId,
						promptCount: results.length,
						personaCount: personas.length,
					})
				}
			/>
		);
	} else if (phase !== "input") {
		body = (
			<GeneratingPanel
				domain={
					generation?.domain ?? (restored ? null : domain || null)
				}
				step={step}
				includePersonas={generation?.includePersonas ?? includePersonas}
				frameworkCount={
					generation?.frameworks.length ?? selectedFrameworks.size
				}
			/>
		);
	} else {
		body = (
			<form onSubmit={handleSubmit} className="w-full space-y-6">
				<div className="space-y-4">
					<div>
						<label
							htmlFor="prompt-generator-domain"
							className="mb-1.5 block text-sm font-medium"
						>
							Website Domain
						</label>
						<Input
							id="prompt-generator-domain"
							type="text"
							placeholder="example.com"
							value={domain}
							onChange={(e) => {
								setDomain(e.target.value);
								setValidationError(null);
							}}
							className="text-base"
						/>
					</div>

					<div>
						<p className="mb-1.5 block text-sm font-medium">
							Location{" "}
							<span className="font-normal text-muted-foreground">
								(optional)
							</span>
						</p>
						<LocationInput
							value={location}
							onChange={setLocation}
							placeholder="Search for a city..."
						/>
					</div>
				</div>

				<fieldset>
					<legend className="mb-2 block text-sm font-medium">
						Prompt Frameworks
					</legend>
					<div className="space-y-2">
						{PROMPT_GENERATOR_FRAMEWORKS.map((fw) => (
							<label
								key={fw}
								htmlFor={`prompt-generator-fw-${fw}`}
								className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/50"
							>
								<Checkbox
									id={`prompt-generator-fw-${fw}`}
									checked={selectedFrameworks.has(fw)}
									onCheckedChange={() => toggleFramework(fw)}
									className="mt-0.5"
								/>
								<div className="min-w-0 flex-1">
									<div className="text-sm font-medium">
										{PROMPT_GENERATOR_FRAMEWORK_LABELS[fw]}
									</div>
									<div className="mt-0.5 text-xs text-muted-foreground">
										{FRAMEWORK_FORM_DESCRIPTIONS[fw]}
									</div>
								</div>
							</label>
						))}
					</div>
				</fieldset>

				<div>
					<p className="mb-2 block text-sm font-medium">
						{PERSONA_OPTION.label}
					</p>
					<div
						className={cn(
							"flex items-start gap-3 rounded-lg border p-3 transition-colors",
							includePersonas && "border-primary/40 bg-primary/5",
						)}
					>
						<UsersIcon
							className="mt-0.5 size-4 shrink-0 text-primary"
							aria-hidden
						/>
						<div className="min-w-0 flex-1">
							<label
								htmlFor={personaSwitchId}
								className="flex cursor-pointer flex-wrap items-center gap-2 text-sm font-medium"
							>
								{PERSONA_OPTION.title}
								<Badge
									status="info"
									className="px-2 py-0.5 text-[10px]"
								>
									New
								</Badge>
							</label>
							<p className="mt-0.5 text-xs text-muted-foreground">
								{PERSONA_OPTION.body}
							</p>
						</div>
						<Switch
							id={personaSwitchId}
							checked={includePersonas}
							onCheckedChange={setIncludePersonas}
							className="mt-0.5"
							aria-label="Include personas"
						/>
					</div>
				</div>

				{validationError ? (
					<p
						role="alert"
						className="flex flex-wrap items-center gap-2 text-sm"
					>
						<Badge
							status="error"
							className="px-2 py-0.5 text-[10px]"
						>
							Error
						</Badge>
						<span className="text-foreground">
							{validationError}
						</span>
					</p>
				) : null}

				<Button
					type="submit"
					size="lg"
					className="h-auto min-h-11 w-full py-2.5 text-center"
					disabled={
						generateMutation.isPending ||
						selectedFrameworks.size === 0
					}
				>
					{generateMutation.isPending ? (
						<>
							<Loader2Icon className="mr-2 size-4 animate-spin motion-reduce:animate-none" />
							Starting...
						</>
					) : (
						<>
							<SparklesIcon className="mr-2 size-4" />
							{includePersonas
								? "Generate Prompts and Personas"
								: `Generate Up to ${count} AI Visibility Prompts`}
						</>
					)}
				</Button>

				<p className="text-center text-xs text-muted-foreground">
					{includePersonas
						? `Up to ${count} prompts and 3 to 5 personas. Free. No account required.`
						: "Free. No account required. We'll analyze your brand and generate prompts in under 90 seconds."}
				</p>
			</form>
		);
	}

	return (
		<div
			ref={formRef}
			id="prompt-generator"
			className={cn(
				"mx-auto scroll-mt-28 transition-[max-width]",
				showResults ? "max-w-6xl" : "max-w-2xl",
			)}
		>
			<div className="rounded-2xl border bg-card p-4 shadow-sm sm:p-6">
				{body}
			</div>
		</div>
	);
}
